const MAX_LOGO_DIMENSION = 600;
const MAX_FILE_SIZE_BYTES = 15 * 1024 * 1024; // 15MB

class SenteMockupTool extends HTMLElement {
  connectedCallback() {
    this.fileInput = this.querySelector('[data-mockup-upload]');
    this.triggerButton = this.querySelector('[data-mockup-trigger]');
    this.clearButton = this.querySelector('[data-mockup-clear]');
    this.resetButton = this.querySelector('[data-mockup-reset]');
    this.sizeSlider = this.querySelector('[data-mockup-size]');
    this.logos = Array.from(this.querySelectorAll('.sente-mockup-logo'));
    this.fileNameEl = this.querySelector('[data-mockup-filename]');
    this.errorEl = this.querySelector('[data-mockup-error]');

    if (this.triggerButton && this.fileInput) {
      this.triggerButton.addEventListener('click', () => this.fileInput.click());
    }
    if (this.fileInput) {
      this.fileInput.addEventListener('change', (event) => this.handleUpload(event));
    }
    if (this.clearButton) {
      this.clearButton.addEventListener('click', () => this.clearLogo());
    }
    if (this.resetButton) {
      this.resetButton.addEventListener('click', () => this.resetPositions());
    }
    if (this.sizeSlider) {
      this.sizeSlider.addEventListener('input', (event) => {
        this.style.setProperty('--sente-mockup-scale', event.target.value);
      });
    }

    this.logos.forEach((logo) => this.makeDraggable(logo));
  }

  showError(message) {
    if (this.errorEl) {
      this.errorEl.textContent = message;
      this.errorEl.hidden = !message;
    }
  }

  async handleUpload(event) {
    const file = event.target.files && event.target.files[0];
    if (!file) return;

    this.showError('');

    if (!file.type.startsWith('image/')) {
      this.showError('Please choose an image file (PNG, JPG, SVG or WEBP).');
      this.fileInput.value = '';
      return;
    }

    if (file.size > MAX_FILE_SIZE_BYTES) {
      this.showError('That image is too large — please use a file under 15MB.');
      this.fileInput.value = '';
      return;
    }

    try {
      const dataUrl = await this.readFileAsDataUrl(file);
      const safeDataUrl = await this.downscaleImage(dataUrl, file.type);

      this.logos.forEach((logo) => {
        logo.src = safeDataUrl;
        logo.hidden = false;
      });
      this.classList.add('has-logo');
      if (this.fileNameEl) {
        this.fileNameEl.textContent = file.name;
      }
    } catch (error) {
      this.showError('Sorry, that image could not be loaded. Please try a different file.');
      this.fileInput.value = '';
    }
  }

  readFileAsDataUrl(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    });
  }

  downscaleImage(dataUrl, mimeType) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const { naturalWidth: w, naturalHeight: h } = img;

        if (!w || !h) {
          resolve(dataUrl);
          return;
        }

        if (w <= MAX_LOGO_DIMENSION && h <= MAX_LOGO_DIMENSION) {
          resolve(dataUrl);
          return;
        }

        const scale = MAX_LOGO_DIMENSION / Math.max(w, h);
        const targetW = Math.round(w * scale);
        const targetH = Math.round(h * scale);

        const canvas = document.createElement('canvas');
        canvas.width = targetW;
        canvas.height = targetH;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          resolve(dataUrl);
          return;
        }
        ctx.drawImage(img, 0, 0, targetW, targetH);

        const outputType = mimeType === 'image/svg+xml' ? 'image/png' : mimeType;
        try {
          resolve(canvas.toDataURL(outputType === 'image/png' || outputType === 'image/webp' ? outputType : 'image/png'));
        } catch (error) {
          resolve(dataUrl);
        }
      };
      img.onerror = () => reject(new Error('Could not decode image'));
      img.src = dataUrl;
    });
  }

  clearLogo() {
    this.showError('');
    this.logos.forEach((logo) => {
      logo.hidden = true;
      logo.removeAttribute('src');
    });
    this.classList.remove('has-logo');
    if (this.fileInput) {
      this.fileInput.value = '';
    }
    if (this.fileNameEl) {
      this.fileNameEl.textContent = '';
    }
  }

  resetPositions() {
    this.logos.forEach((logo) => {
      logo.style.left = `${logo.dataset.defaultX}%`;
      logo.style.top = `${logo.dataset.defaultY}%`;
    });
    if (this.sizeSlider) {
      this.sizeSlider.value = '1';
      this.style.setProperty('--sente-mockup-scale', '1');
    }
  }

  makeDraggable(logo) {
    const card = logo.closest('.sente-mockup-card');
    if (!card) return;

    let dragging = false;

    const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

    const setPosition = (clientX, clientY) => {
      const rect = card.getBoundingClientRect();
      const x = clamp(((clientX - rect.left) / rect.width) * 100, 0, 100);
      const y = clamp(((clientY - rect.top) / rect.height) * 100, 0, 100);
      logo.style.left = `${x}%`;
      logo.style.top = `${y}%`;
    };

    logo.addEventListener('pointerdown', (event) => {
      if (logo.hidden) return;
      dragging = true;
      logo.setPointerCapture(event.pointerId);
      logo.classList.add('is-dragging');
      event.preventDefault();
    });

    logo.addEventListener('pointermove', (event) => {
      if (!dragging) return;
      setPosition(event.clientX, event.clientY);
    });

    const endDrag = () => {
      dragging = false;
      logo.classList.remove('is-dragging');
    };

    logo.addEventListener('pointerup', endDrag);
    logo.addEventListener('pointercancel', endDrag);
  }
}

if (!customElements.get('sente-mockup-tool')) {
  customElements.define('sente-mockup-tool', SenteMockupTool);
}
